import { Button, PreIcon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import {
    addPresenceSubscriptionPromo,
    assignAndActivateMarketingPromo,
    checkProviderPromoEligibility,
    deletePresenceSubscriptionPromo,
    fetchProvider,
    fetchProviderPromos,
} from 'actions/providers'
import { useToast } from 'components/misc/Toaster'
import AddActiveMarketingPromoModal from 'components/providers/AddActiveMarketingPromoModal'
import AddProviderPromoModal from 'components/providers/AddProviderPromoModal'
import DeletePromoModal from 'components/providers/DeletePromoModal'
import ProviderPromosList from 'components/providers/ProviderPromosList'
import { useAppDispatch } from 'hooks/hooks'
import { ProviderPromo } from 'interfaces/ProviderPromos'
import { hasAccess, isSuperUser } from 'misc/user'
import { useEffect, useRef, useState } from 'react'

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (typeof error === 'object' && error !== null) {
        const { message, error: errorField } = error as { message?: unknown; error?: unknown }
        if (typeof message === 'string' && message) {
            return message
        }
        if (typeof errorField === 'string' && errorField) {
            return errorField
        }
    }
    return fallback
}

interface MarketingPromosProps {
    providerId: number
    providerName?: string
}

const MarketingPromos = ({ providerId, providerName }: MarketingPromosProps) => {
    const { addToast } = useToast()
    const dispatch = useAppDispatch()
    const [promos, setPromos] = useState<ProviderPromo[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [canAddPromo, setCanAddPromo] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const fetchedProviderIdRef = useRef<number | null>(null)
    const isFetchingRef = useRef(false)

    // Fetch promos from provider - only once per providerId
    useEffect(() => {
        // Skip if already fetched for this providerId or currently fetching
        if (fetchedProviderIdRef.current === providerId || isFetchingRef.current) {
            return
        }

        const loadPromos = async () => {
            // Prevent multiple concurrent calls
            if (isFetchingRef.current) {
                return
            }

            try {
                isFetchingRef.current = true
                setIsLoading(true)
                setError(null) // Clear previous error
                const [fetchedPromos, eligibility] = await Promise.all([
                    dispatch(fetchProviderPromos(providerId)),
                    dispatch(checkProviderPromoEligibility(providerId)),
                ])
                setPromos(fetchedPromos)
                setCanAddPromo(eligibility.canAddPromo || false)
                fetchedProviderIdRef.current = providerId
            } catch (error) {
                const errorMessage = getErrorMessage(error, 'Failed to fetch promos')

                // Mark as fetched to prevent automatic retries, but track error state
                fetchedProviderIdRef.current = providerId
                setError(errorMessage)

                addToast({
                    msg: errorMessage,
                    red: true,
                    icon: 'error',
                })
                setPromos([])
                setCanAddPromo(false)
            } finally {
                setIsLoading(false)
                isFetchingRef.current = false
            }
        }

        loadPromos()
    }, [providerId, dispatch, addToast])

    const refreshPromos = async () => {
        try {
            setError(null) // Clear previous error
            const [fetchedPromos, eligibility] = await Promise.all([
                dispatch(fetchProviderPromos(providerId)),
                dispatch(checkProviderPromoEligibility(providerId)),
            ])
            setPromos(fetchedPromos)
            setCanAddPromo(eligibility.canAddPromo || false)
        } catch (error) {
            const errorMessage = getErrorMessage(error, 'Failed to refresh promos')
            setError(errorMessage)
            addToast({
                msg: errorMessage,
                red: true,
                icon: 'error',
            })
        }
    }

    const addPromo = async () => {
        await NiceModal.show(AddProviderPromoModal, {
            providerId,
            providerName: providerName || 'this provider',
            onConfirm: async (data) => {
                try {
                    await dispatch(addPresenceSubscriptionPromo(providerId, data))
                    addToast({
                        msg: 'Promo added successfully',
                        green: true,
                        icon: 'check_circle',
                    })
                    // Refresh promos list
                    await refreshPromos()
                } catch (error) {
                    addToast({
                        msg: getErrorMessage(error, 'Failed to add promo'),
                        red: true,
                        icon: 'error',
                    })
                }
            },
            onCancel: () => {},
        })
    }

    const addActiveMarketingPromo = async () => {
        await NiceModal.show(AddActiveMarketingPromoModal, {
            providerId,
            providerName: providerName || 'this provider',
            canOverride: isSuperUser() || hasAccess('admin', 'w'),
            onConfirm: async ({ promoId, override }) => {
                await dispatch(assignAndActivateMarketingPromo(providerId, { promoId, override }))
                addToast({
                    msg: 'Marketing promo activated',
                    green: true,
                    icon: 'check_circle',
                })
                // Reflect the new active promo and this month's free leads immediately.
                await refreshPromos()
                dispatch(fetchProvider(providerId, true))
            },
            onCancel: () => {},
        })
    }

    const deletePromo = async (promoId: number) => {
        await NiceModal.show(DeletePromoModal, {
            onConfirm: async () => {
                try {
                    await dispatch(deletePresenceSubscriptionPromo(providerId, promoId))
                    addToast({
                        msg: 'Promo deleted successfully',
                        green: true,
                        icon: 'check_circle',
                    })
                    // Refresh promos list
                    await refreshPromos()
                } catch (error) {
                    addToast({
                        msg: getErrorMessage(error, 'Failed to delete promo'),
                        red: true,
                        icon: 'error',
                    })
                }
            },
            onCancel: () => {},
        })
    }

    if (isLoading) {
        return <div>Loading promos...</div>
    }

    // Check if provider has any active promos (inactive promos don't prevent adding new ones)
    const hasActivePromo = promos.some((promo) => promo.status === 'active')

    // Show button only if eligible AND no active promos exist
    const showAddButton = canAddPromo && !hasActivePromo

    return (
        <div>
            {error && (
                <div
                    style={{
                        marginBottom: '1rem',
                        padding: '0.5rem',
                        backgroundColor: '#fee',
                        color: '#c33',
                        borderRadius: '4px',
                    }}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            <ProviderPromosList promos={promos} onDeletePromo={deletePromo} />

            <div className="flex flex-col items-start">
                {showAddButton && (
                    <Button name="text" onClick={addPromo}>
                        <PreIcon primColor icon="add">
                            Add pending marketing promo
                        </PreIcon>
                    </Button>
                )}

                {hasAccess('providers', 'w') && (
                    <Button name="text" onClick={addActiveMarketingPromo}>
                        <PreIcon primColor icon="redeem">
                            Add active marketing promo
                        </PreIcon>
                    </Button>
                )}
            </div>
        </div>
    )
}

export default MarketingPromos
